import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { z } from 'zod'

import { useTranslation } from '@flecto/translations'
import { type CoreLocale } from '@flecto/types'

import { useStoreBasket } from '../../hooks/useStoreBasket'
import { useStoreDates } from '../../hooks/useStoreDates'
import { type OrderForm } from '../../types/OrderForm'

dayjs.extend(isSameOrBefore)

export function useCheckoutSchema() {
  const { t, i18n } = useTranslation('store', {
    keyPrefix: 'store-page-checkout',
  })
  const { t: tv } = useTranslation('store', { keyPrefix: 'store-shared' })
  const { products } = useStoreBasket()
  const { startAt, endAt } = useStoreDates()

  const schema = z
    .object({
      start_at: z.string().min(2, { message: tv('validations.required') }),
      end_at: z.string().min(2, { message: tv('validations.required') }),
      email: z.string().email({ message: tv('validations.invalid_email') }),
      full_name: z.string().min(2, { message: tv('validations.required') }),
      flexible_dates: z.boolean(),
      renter_message: z
        .string()
        .min(2, { message: tv('validations.required') }),
      phone_number: z.string(),
      _phone_number: z.string().min(2, { message: tv('validations.required') }),
      _phone_number_country_code: z.string(),
      locale: z.string(),
    })
    .refine(
      (data) => {
        if (!data.end_at) return true
        return dayjs(data.start_at).isSameOrBefore(dayjs(data.end_at))
      },
      {
        path: ['start_at'],
        message: tv('validations.start_after_end'),
      }
    )

  const initialValues: Partial<OrderForm> = {
    start_at: startAt,
    end_at: endAt,
    phone_number: '',
    _phone_number: '',
    _phone_number_country_code: '',
    full_name: '',
    email: '',
    flexible_dates: true,
    renter_message: t('card_your_info.default_message'),
    locale: i18n.language as CoreLocale,
    inquiry_items_attributes: products.map((i) => ({
      item_id: i.product.id,
      quantity: i.quantity,
    })),
  }

  return { schema, initialValues }
}
